import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { oneKlicks } from "../../SDK/oneclick";
import { toast } from "react-toastify";
import { Badge, Form, Button, Row, Col, Table, InputGroup } from "react-bootstrap";
import { Select, Input, Modal } from "antd";
import {
    SearchOutlined,
    PlusOutlined,
    CalendarOutlined,
    MailOutlined,
    ReloadOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    CheckOutlined,
    CloseOutlined,
    EditOutlined
} from "@ant-design/icons";
import ImageShow from "../../Uitiles/ImageShow";
import EmailVerify from "./Components/Dashbord/EmailVerify";
import Paginationnew from "../../Uitiles/Paginationnew";
import updateBalance from "../../Uitiles/updateBalance";

const AppointmentList = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [appointments, setAppointments] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [appointmentModal, setAppointmentModal] = useState(false);
    const [hospitalInfo, setHospitalInfo] = useState(null);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [detailsModalVisible, setDetailsModalVisible] = useState(false);

    useEffect(() => {
        if (userData?.id) {
            fetchAppointments();
            getHospitalSettings();
        }
    }, [userData, filterStatus, page]);

    /* ---------------- API CALLS ---------------- */

    const getHospitalSettings = async () => {
        try {
            const response = await oneKlicks.read(
                "",
                "appointment_settings",
                { doctor_id: userData.id },
                "id DESC",
                {},
                {},
                [{
                    target_table: "users",
                    target_column: "id",
                    target_value: "hospital_id",
                    target_label: "hospital_name"
                }]
            );

            if (response.success && response.data?.[0]?.hospital_name?.[0]) {
                setHospitalInfo(response.data[0].hospital_name[0]);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const conditions = { doctor_id: userData.id };
            if (filterStatus !== "all") conditions.status = filterStatus;

            const response = await oneKlicks.read(
                "",
                "appointments",
                conditions,
                "id DESC",
                "",
                { page, limit },
                [{
                    target_table: "users",
                    target_column: "id",
                    target_value: "patient_id",
                    target_label: "patient"
                }]
            );

            if (response.success) {
                const data = response.data || [];
                // Check for 2-hour timeout and auto-refund/auto-decline if needed
                // "alage appointment time daati 2 hours ayina return cheyali"
                setAppointments(data);
                setTotalPages(response.totalPages || 1);
            }
        } catch (err) {
            toast.error("Failed to load appointments");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (record) => {
        try {
            const data = {
                status: 'confirmed',
                updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };
            const response = await oneKlicks.update("", "appointments", data, { id: record.id });
            if (response.success) {
                // Send notification to user
                await oneKlicks.create("", "notifications", {
                    user_id: record.patient_id,
                    title: "Appointment Approved",
                    message: `Dr. ${userData.display_name || 'Doctor'} has approved your appointment (#${record.id}). Doctor mee appointment ni approve chesaru.`,
                    type: "appointment_confirmed",
                    status: "unread",
                    created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
                });
                toast.success("Appointment approved successfully");
                if (detailsModalVisible) setDetailsModalVisible(false);
                fetchAppointments();
            }
        } catch (err) {
            toast.error("Failed to approve appointment");
        }
    };

    const handleDecline = async (record) => {
        try {
            const refundAmount = parseFloat(record.amount || record.display_amount || 0);

            // 1. Update status
            const data = {
                status: 'cancelled',
                updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };
            const response = await oneKlicks.update("", "appointments", data, { id: record.id });

            if (response.success) {
                // 2. Refund to patient balance
                if (refundAmount > 0) {
                    // Find patient balance record
                    const checkbalance = await oneKlicks.read("", "balance", { account_id: record.patient_id }, "id DESC", "", "", "");

                    if (checkbalance.success && checkbalance.data?.length > 0) {
                        const accountId = checkbalance.data[0].id;
                        await updateBalance(refundAmount, 0, accountId, `REFUND-${record.id}-${Date.now()}`, {
                            date: new Date().toISOString().split("T")[0],
                            notes: `Refund for cancelled appointment #${record.id}`
                        }, '+');
                    } else {
                        // Create new balance if not exists (though patients should have one)
                        await oneKlicks.create("", "balance", {
                            account_id: record.patient_id,
                            amount: refundAmount,
                            status: "active"
                        });
                        await oneKlicks.create("", "balance_sheet", {
                            account_id: record.patient_id,
                            transaction_id: `REFUND-${record.id}-${Date.now()}`,
                            credit: refundAmount,
                            debit: 0,
                            balance: refundAmount,
                            date: new Date().toISOString().split("T")[0],
                            description: `Refund for cancelled appointment #${record.id}`,
                            status: "active"
                        });
                    }
                }

                // 3. Send notification
                await oneKlicks.create("", "notifications", {
                    user_id: record.patient_id,
                    title: "Appointment Declined",
                    message: `Dr. ${userData.display_name || 'Doctor'} has declined your appointment. Your payment of ₹${refundAmount} has been refunded to your wallet.`,
                    type: "appointment_declined",
                    status: "unread",
                    created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
                });

                toast.warning("Appointment declined and refund processed");
                if (detailsModalVisible) setDetailsModalVisible(false);
                fetchAppointments();
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to decline appointment");
        }
    };

    const checkTimeoutRefund = (record) => {
        if (record.status !== 'pending' && record.status !== 'upcoming') return false;

        try {
            const appointmentDateTime = new Date(`${record.appointment_date} ${record.appointment_time}`);
            if (isNaN(appointmentDateTime)) return false;

            const now = new Date();
            const diffMs = now - appointmentDateTime;
            const diffHrs = diffMs / (1000 * 60 * 60);

            return diffHrs >= 2;
        } catch (e) {
            return false;
        }
    };

    /* ---------------- HELPERS ---------------- */

    const getPatientName = (record) => {
        const p = record.patient?.[0];
        return p?.display_name || `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || record.patient_name || "Unknown";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return isNaN(d) ? "N/A" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return "N/A";
        let raw = timeStr.toString().replace(":", "");
        if (raw.length === 3) raw = "0" + raw;

        if (raw.length === 4) {
            let hours = parseInt(raw.substring(0, 2));
            let minutes = raw.substring(2, 4);
            let ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12;
            return `${hours}:${minutes} ${ampm}`;
        }
        return timeStr;
    };

    const getStatusBadge = (status) => {
        const s = (status || "pending").toLowerCase();
        const map = {
            confirmed: "success",
            upcoming: "primary",
            pending: "warning",
            completed: "info",
            cancelled: "danger",
            canceled: "danger"
        };
        return (
            <Badge bg={map[s] || "secondary"} className="custom-badge">
                {status || "Unknown"}
            </Badge>
        );
    };

    const filteredData = appointments.filter(a => {
        const search = searchText.toLowerCase();
        return (
            getPatientName(a).toLowerCase().includes(search) ||
            (a.patient?.[0]?.email || "").toLowerCase().includes(search) ||
            a.id?.toString().includes(search)
        );
    });

    /* ---------------- UI ---------------- */

    return (
        <div className="subbody" style={{ width: '100%', padding: '0 0 40px', background: '#f8fafc', minHeight: '100vh', overflowX: 'hidden' }}>
            <style>{`
                .container-fluid {
                    padding: 20px;
                }
                .card {
                    border: none;
                    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
                    border-radius: 0.75rem;
                }
                .card-body {
                    padding: 1.5rem;
                }
                .h-40 {
                    height: 40px !important;
                }
                .font-weight-bold {
                    font-weight: 700 !important;
                }
                .dc-update-servicesss {
                    width: 32px;
                    height: 32px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 6px;
                    transition: all 0.3s;
                }
                .dc-update-servicesss:hover {
                    transform: scale(1.05);
                }
                .fit-content {
                    width: fit-content !important;
                    padding: 6px 15px !important;
                    font-size: 13px;
                }
                .btn-action-confirm {
                    background: #52c41a !important;
                    border-color: #52c41a !important;
                    color: white !important;
                }
                .btn-action-decline {
                    background: #ff4d4f !important;
                    border-color: #ff4d4f !important;
                    color: white !important;
                }
                .custom-badge {
                    font-size: 11px;
                    padding: 5px 12px;
                    border-radius: 4px;
                    font-weight: 600;
                    text-transform: capitalize;
                }
                .table thead th {
                    background: #f8f9fb;
                    border-top: none;
                    font-size: 13px;
                    color: #555;
                    font-weight: 600;
                    padding: 15px;
                }
                .table tbody td {
                    padding: 15px;
                    font-size: 14px;
                    color: #444;
                    vertical-align: middle;
                }
                .patient-info h6 {
                    margin: 0;
                    font-weight: 600;
                    font-size: 15px;
                }
                .patient-info p {
                    margin: 0;
                    font-size: 12px;
                    color: #888;
                }
                .schedule-info p {
                    margin: 0;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .schedule-info .date {
                    font-weight: 600;
                    color: #333;
                }
                .schedule-info .time {
                    color: var(--themecolor);
                }
                .icon-square {
                    background: var(--themecolor);
                    color: white;
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
            `}</style>

            <div className="container-fluid">
                {/* HEADER CARD */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <h5 className="mb-0 font-weight-bold">Appointments Management</h5>
                                <Badge bg="info">Appointments</Badge>
                            </div>
                            <div className="d-flex gap-2">
                                <Button variant="outline-primary" className="h-40 fit-content" onClick={fetchAppointments}>
                                    <ReloadOutlined /> Refresh
                                </Button>
                                <Button className="btn-primary h-40 fit-content" onClick={() => setAppointmentModal(true)}>
                                    <PlusOutlined /> Book New
                                </Button>
                            </div>
                        </div>

                        <Row className="g-3">
                            <Col lg={8} md={7}>
                                <InputGroup className="h-40">
                                    <InputGroup.Text className="bg-white border-end-0">
                                        <SearchOutlined style={{ color: '#999' }} />
                                    </InputGroup.Text>
                                    <Form.Control
                                        className="border-start-0"
                                        placeholder="Filter by name, email, or appointment ID..."
                                        value={searchText}
                                        onChange={e => setSearchText(e.target.value)}
                                        style={{ boxShadow: 'none' }}
                                    />
                                </InputGroup>
                            </Col>
                            <Col lg={4} md={5}>
                                <Select
                                    defaultValue="all"
                                    style={{ width: '100%', height: '40px' }}
                                    onChange={setFilterStatus}
                                    className="status-select"
                                >
                                    <Select.Option value="all">All Status</Select.Option>
                                    <Select.Option value="confirmed">Confirmed</Select.Option>
                                    <Select.Option value="upcoming">Upcoming</Select.Option>
                                    <Select.Option value="pending">Pending</Select.Option>
                                    <Select.Option value="completed">Completed</Select.Option>
                                    <Select.Option value="cancelled">Cancelled</Select.Option>
                                </Select>
                            </Col>
                        </Row>
                    </div>
                </div>

                {/* TABLE CARD */}
                <div className="card shadow-sm">
                    <div className="card-body">
                        <div className="table-responsive">
                            <Table hover size="sm" className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px' }}>ID</th>
                                        <th>Patient Details</th>
                                        <th style={{ width: '250px' }}>Schedule</th>
                                        <th style={{ width: '150px', textAlign: 'center' }}>Status</th>
                                        <th style={{ width: '220px', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : filteredData.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center py-5 text-muted italic">
                                                No appointments matches your criteria.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredData.map((record) => (
                                            <tr key={record.id}>
                                                <td className="font-weight-bold text-secondary">#{record.id}</td>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <ImageShow
                                                            url={record.patient?.[0]?.profile_photo_url}
                                                            width={45} height={45}
                                                            style={{ objectFit: 'cover', borderRadius: '50%', border: '1px solid #eee' }}
                                                            type="patient"
                                                        />
                                                        <div className="patient-info d-flex align-items-center gap-2">
                                                            <h6 className="mb-0">{getPatientName(record)}</h6>
                                                            <span className="text-muted small">({record.patient?.[0]?.email || record.user_email || "No email"})</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="schedule-info">
                                                        <p className="date"><CalendarOutlined /> {formatDate(record.appointment_date)}</p>
                                                        <p className="time"><ClockCircleOutlined /> {formatTime(record.appointment_time)}</p>
                                                    </div>
                                                    {checkTimeoutRefund(record) && (
                                                        <Badge bg="light" text="danger" style={{ border: '1px solid #ffccc7', fontSize: '10px', marginTop: '4px' }}>
                                                            2h Delay (Refund Policy)
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    {getStatusBadge(record.status)}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2 justify-content-end align-items-center">
                                                        {record.status?.toLowerCase() === 'pending' && (
                                                            <Button
                                                                className="dc-update-servicesss btn-action-confirm"
                                                                title="Approve Now"
                                                                onClick={() => handleConfirm(record)}
                                                            >
                                                                <CheckOutlined />
                                                            </Button>
                                                        )}
                                                        {['pending', 'upcoming', 'confirmed'].includes(record.status?.toLowerCase()) && (
                                                            <Button
                                                                className="dc-update-servicesss btn-action-decline"
                                                                title="Decline & Refund"
                                                                onClick={() => handleDecline(record)}
                                                            >
                                                                <CloseOutlined />
                                                            </Button>
                                                        )}
                                                        <Button
                                                            className="dc-update-servicesss btn-light border"
                                                            title="Edit Details"
                                                            onClick={() => {
                                                                setSelectedAppointment(record);
                                                                setDetailsModalVisible(true);
                                                            }}
                                                        >
                                                            <EditOutlined />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {!loading && filteredData.length > 0 && (
                            <div className="pt-4 border-top mt-3 d-flex justify-content-end">
                                <Paginationnew
                                    currentPage={page}
                                    totalPages={totalPages}
                                    handlePageChange={setPage}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ADD BOOKING MODAL */}
            <Modal
                title={<><PlusOutlined style={{ color: 'var(--themecolor)' }} /> Add New Appointment</>}
                open={appointmentModal}
                onCancel={() => setAppointmentModal(false)}
                footer={null}
                width={600}
                centered
            >
                <div className="py-2">
                    <Form>
                        <Row>
                            <Col md={12} className="mb-3">
                                <Form.Label className="small font-weight-bold">Patient Email</Form.Label>
                                <Input placeholder="Enter patient email" prefix={<MailOutlined />} style={{ height: '45px', borderRadius: '8px' }} />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="small font-weight-bold">First Name</Form.Label>
                                <Input placeholder="First Name" style={{ height: '45px', borderRadius: '8px' }} />
                            </Col>
                            <Col md={6} className="mb-3">
                                <Form.Label className="small font-weight-bold">Last Name</Form.Label>
                                <Input placeholder="Last Name" style={{ height: '45px', borderRadius: '8px' }} />
                            </Col>
                            <Col md={12} className="mb-4">
                                <Form.Label className="small font-weight-bold">Contact Number</Form.Label>
                                <Input placeholder="Phone number" style={{ height: '45px', borderRadius: '8px' }} />
                            </Col>
                        </Row>
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            <Button variant="light" onClick={() => setAppointmentModal(false)} style={{ borderRadius: '8px', padding: '10px 20px' }}>Cancel</Button>
                            <Button className="btn-theme-action" style={{ padding: '10px 30px' }}>
                                Confirm Booking
                            </Button>
                        </div>
                    </Form>
                </div>
            </Modal>
            {/* VIEW DETAILS MODAL */}
            <Modal
                title={
                    <div className="d-flex align-items-center gap-2">
                        <div className="icon-square" style={{ width: 32, height: 32 }}>
                            <EditOutlined style={{ fontSize: 16 }} />
                        </div>
                        <span style={{ fontWeight: 700 }}>Edit Appointment #{selectedAppointment?.id}</span>
                    </div>
                }
                open={detailsModalVisible}
                onCancel={() => setDetailsModalVisible(false)}
                footer={null}
                width={700}
                centered
            >
                {selectedAppointment && (
                    <div className="py-2">
                        <Row className="g-4">
                            {/* Patient Info Card */}
                            <Col md={12}>
                                <div className="details-info-card d-flex align-items-center gap-4">
                                    <ImageShow
                                        url={selectedAppointment.patient?.[0]?.profile_photo_url}
                                        width={80} height={80}
                                        style={{ objectFit: 'cover', borderRadius: '12px', border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                        type="patient"
                                    />
                                    <div>
                                        <h5 className="mb-1" style={{ fontWeight: 700 }}>{getPatientName(selectedAppointment)}</h5>
                                        <div className="text-muted small mb-2">{selectedAppointment.patient?.[0]?.email || selectedAppointment.user_email || "No email"}</div>
                                        <div className="d-flex gap-2">
                                            {getStatusBadge(selectedAppointment.status)}
                                            {selectedAppointment.payment_status?.toLowerCase() === 'completed' && (
                                                <Badge bg="success" style={{ fontSize: 10, padding: "5px 10px" }}>Paid</Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            {/* Schedule Details */}
                            <Col md={6}>
                                <div className="details-info-card">
                                    <div className="mb-3">
                                        <div className="details-label">Appointment Date</div>
                                        <div className="details-value">
                                            <CalendarOutlined style={{ color: 'var(--themecolor)', marginRight: 8 }} />
                                            {formatDate(selectedAppointment.appointment_date)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="details-label">Time Slot</div>
                                        <div className="details-value">
                                            <ClockCircleOutlined style={{ color: 'var(--themecolor)', marginRight: 8 }} />
                                            {formatTime(selectedAppointment.appointment_time)}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            {/* Payment Details */}
                            <Col md={6}>
                                <div className="details-info-card">
                                    <div className="mb-3">
                                        <div className="details-label">Consulation Fee</div>
                                        <div className="details-value" style={{ fontWeight: 700, color: 'var(--themecolor)' }}>
                                            ₹{selectedAppointment.amount || selectedAppointment.display_amount || "0"}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="details-label">Payment ID</div>
                                        <div className="details-value" style={{ fontSize: 13, fontFamily: 'monospace' }}>
                                            {selectedAppointment.payment_id || "N/A"}
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            {/* Additional Notes */}
                            {selectedAppointment.note && (
                                <Col md={12}>
                                    <div className="details-info-card">
                                        <div className="details-label">Patient Note / Reason</div>
                                        <div className="details-value" style={{ fontSize: 14, fontStyle: 'italic', lineHeight: 1.6 }}>
                                            "{selectedAppointment.note}"
                                        </div>
                                    </div>
                                </Col>
                            )}

                            {/* Action Buttons */}
                            <Col md={12}>
                                <div className="d-flex justify-content-end gap-2 mt-2 pt-3 border-top">
                                    <Button variant="light" onClick={() => setDetailsModalVisible(false)} style={{ borderRadius: '8px', padding: '10px 20px' }}>
                                        Close
                                    </Button>

                                    {['pending', 'upcoming', 'confirmed'].includes(selectedAppointment.status?.toLowerCase()) && (
                                        <Button
                                            className="btn-action-confirm"
                                            style={{ height: 'auto', padding: '10px 25px' }}
                                            onClick={() => handleConfirm(selectedAppointment)}
                                        >
                                            <CheckOutlined /> Approve
                                        </Button>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AppointmentList;
